import React from 'react';
import { Link } from 'gatsby';

import SEO from '../../components/seo';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

import plantConveyor from '../../images/kalinga/plant-conveyor.jpeg';

import '../../components/layout.css';

const KalingaPage = () => (
  <div className="theme-default bg-background text-foreground">
    <SEO title="Project Kalinga" />
    <Header />

    <main>
      {/* OCF mainHero */}
      <section className="section">
        <div className="max-w-screen-xpad mx-auto px-4 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-7 md:col-start-1">
              <span className="chip mb-6 inline-flex">Operational</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight tracking-tight mt-4 mb-6">
                Project Kalinga
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-2xl mb-8">
                Controlled pyrolysis of rice husk into stable biochar for
                permanent soil carbon storage across Odisha.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/diligence" className="button-split group">
                  <span className="button-split-circle">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </span>
                  <span className="button-split-pill">
                    Request diligence access
                  </span>
                </Link>
              </div>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <div className="rounded overflow-hidden border border-border">
                <img
                  src={plantConveyor}
                  alt="Biomass conveyor at pyrolysis facility"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="h-20 md:h-32" />

      {/* OCF problem/solution */}
      <section className="section">
        <div className="max-w-screen-xpad mx-auto px-4 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start">
            <div className="md:col-span-2 md:col-start-1 mt-2">
              <div className="accent-bar w-8 md:w-16" />
            </div>
            <div className="md:col-span-10 md:col-start-3">
              <h4 className="text-xl md:text-2xl leading-relaxed font-normal">
                Rice husk is an abundant agricultural residue in Odisha.
              </h4>
            </div>
            <div className="md:col-span-2 md:col-start-1 mt-2">
              <div className="accent-bar w-8 md:w-16 opacity-0" />
            </div>
            <div className="md:col-span-10 md:col-start-3">
              <h4 className="text-xl md:text-2xl leading-relaxed font-normal text-muted-foreground">
                We convert it into stable biochar for permanent soil carbon
                storage.
              </h4>
            </div>
          </div>
        </div>
      </section>

      <div className="h-20 md:h-32" />

      {/* OCF twoUpLabelAndText: facts */}
      <section className="section">
        <div className="max-w-screen-xpad mx-auto px-4 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4 md:col-start-1">
              <span className="chip">Key facts</span>
            </div>
            <div className="md:col-span-8 md:col-start-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-background-raised rounded border border-border p-6">
                  <div className="font-mono text-sm text-muted-foreground mb-2">
                    Technology
                  </div>
                  <p className="text-base leading-relaxed">
                    Controlled slow pyrolysis at 450-600&deg;C under low-oxygen
                    conditions
                  </p>
                </div>
                <div className="bg-background-raised rounded border border-border p-6">
                  <div className="font-mono text-sm text-muted-foreground mb-2">
                    Feedstock
                  </div>
                  <p className="text-base leading-relaxed">
                    100% rice husk sourced from partner mills within 5-20 km
                  </p>
                </div>
                <div className="bg-background-raised rounded border border-border p-6">
                  <div className="font-mono text-sm text-muted-foreground mb-2">
                    Permanence
                  </div>
                  <p className="text-base leading-relaxed">
                    200-year durability tier under Isometric Biochar Storage
                    Module
                  </p>
                </div>
                <div className="bg-background-raised rounded border border-border p-6">
                  <div className="font-mono text-sm text-muted-foreground mb-2">
                    Operations
                  </div>
                  <p className="text-base leading-relaxed">
                    Two operational facilities in Kalahandi &amp; Sambalpur
                    districts, Odisha
                  </p>
                </div>
                <div className="bg-background-raised rounded border border-border p-6">
                  <div className="font-mono text-sm text-muted-foreground mb-2">
                    Soil amendment
                  </div>
                  <p className="text-base leading-relaxed">
                    <strong>MatiBoost&#8482;</strong>, a biochar-enhanced soil
                    amendment product connecting durable carbon removal with
                    smallholder farm soil regeneration
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="h-20 md:h-32" />

      {/* OCF twoUpLabelAndText: process link */}
      <section className="section">
        <div className="max-w-screen-xpad mx-auto px-4 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4 md:col-start-1">
              <span className="chip">How it works</span>
            </div>
            <div className="md:col-span-8 md:col-start-5">
              <div className="prose prose-lg max-w-none">
                <p>
                  Project Kalinga follows a five-step process: sourcing rice
                  husk from local partner mills, preparing the feedstock,
                  controlled pyrolysis at 450-600&deg;C, recovering syngas for
                  process heat, and applying biochar to agricultural soils.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 mt-6">
                <Link to="/projects/kalinga/process" className="button-split group">
                  <span className="button-split-circle">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </span>
                  <span className="button-split-pill">
                    View the process
                  </span>
                </Link>
                <Link to="/projects/kalinga/impact" className="button-secondary">
                  View impact
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default KalingaPage;
